<template>
    <form name="form-exhibitor" id="form-exhibitor" class="exhibitor-form" novalidate @submit="onSubmit" @input="markDirty" @change="markDirty">
        <input type="hidden" name="id" v-model="model.id">
        <input type="hidden" name="pic" v-model="model.image">

        <p class="form-required-note"><span aria-hidden="true">*</span> Required fields</p>

        <div v-if="Object.keys(fieldErrors).length" class="form-alert" role="alert" aria-live="assertive">
            <strong>Review the highlighted fields.</strong>
            <span>{{ formErrorMessage }}</span>
        </div>

        <div class="form-row exhibitor-form-sections">
            <section class="col-lg-6 mb-4 mb-lg-0" aria-labelledby="exhibitor-profile-heading">
                <div class="exhibitor-form-section">
                    <div class="section-heading">
                        <div>
                            <p class="section-eyebrow">EXHIBITOR PROFILE</p>
                            <h2 id="exhibitor-profile-heading">Company and product</h2>
                        </div>
                        <i class="pi pi-building" aria-hidden="true"></i>
                    </div>

                    <div class="form-group">
                        <label for="boothNumber">Booth number <span class="required-indicator" aria-hidden="true">*</span></label>
                        <InputText id="boothNumber" v-model="model.boothNumber" class="form-control" :class="{ 'p-invalid': fieldErrors.boothNumber }" aria-describedby="booth-number-help booth-number-error" required />
                        <small id="booth-number-help" class="form-help">Use the booth number assigned in the event floor plan.</small>
                        <small v-if="fieldErrors.boothNumber" id="booth-number-error" class="field-error">{{ fieldErrors.boothNumber }}</small>
                    </div>

                    <div class="form-group">
                        <label for="companyName">Company name <span class="required-indicator" aria-hidden="true">*</span></label>
                        <InputText id="companyName" v-model="model.companyName" class="form-control" :class="{ 'p-invalid': fieldErrors.companyName }" aria-describedby="company-name-error" required />
                        <small v-if="fieldErrors.companyName" id="company-name-error" class="field-error">{{ fieldErrors.companyName }}</small>
                    </div>

                    <div class="form-group">
                        <label for="companyDescription">Company description <span class="required-indicator" aria-hidden="true">*</span></label>
                        <Textarea id="companyDescription" v-model="model.companyDescription" class="form-control" :class="{ 'p-invalid': fieldErrors.companyDescription }" aria-describedby="company-description-help company-description-error" rows="5" required />
                        <small id="company-description-help" class="form-help">Summarize what the company does and why attendees should visit.</small>
                        <small v-if="fieldErrors.companyDescription" id="company-description-error" class="field-error">{{ fieldErrors.companyDescription }}</small>
                    </div>

                    <div class="form-group">
                        <label for="productName">Product name <span class="required-indicator" aria-hidden="true">*</span></label>
                        <InputText id="productName" v-model="model.productName" class="form-control" :class="{ 'p-invalid': fieldErrors.productName }" aria-describedby="product-name-error" required />
                        <small v-if="fieldErrors.productName" id="product-name-error" class="field-error">{{ fieldErrors.productName }}</small>
                    </div>

                    <div class="form-group mb-0">
                        <label for="productDescription">Product description <span class="required-indicator" aria-hidden="true">*</span></label>
                        <Textarea id="productDescription" v-model="model.productDescription" class="form-control" :class="{ 'p-invalid': fieldErrors.productDescription }" aria-describedby="product-description-help product-description-error" rows="5" required />
                        <small id="product-description-help" class="form-help">Describe the product or service delegates can explore at the booth.</small>
                        <small v-if="fieldErrors.productDescription" id="product-description-error" class="field-error">{{ fieldErrors.productDescription }}</small>
                    </div>
                </div>
            </section>

            <section class="col-lg-6" aria-labelledby="exhibitor-contact-heading">
                <div class="exhibitor-form-section">
                    <div class="section-heading">
                        <div>
                            <p class="section-eyebrow">CONTACT AND VISIBILITY</p>
                            <h2 id="exhibitor-contact-heading">Contact and banner</h2>
                        </div>
                        <i class="pi pi-address-book" aria-hidden="true"></i>
                    </div>

                    <div class="form-group">
                        <label for="contactPerson">Contact person <span class="required-indicator" aria-hidden="true">*</span></label>
                        <InputText id="contactPerson" v-model="model.contactPerson" class="form-control" :class="{ 'p-invalid': fieldErrors.contactPerson }" aria-describedby="contact-person-help contact-person-error" required />
                        <small id="contact-person-help" class="form-help">Name the primary representative for this exhibitor.</small>
                        <small v-if="fieldErrors.contactPerson" id="contact-person-error" class="field-error">{{ fieldErrors.contactPerson }}</small>
                    </div>

                    <div class="form-group">
                        <label for="email">Email <span class="required-indicator" aria-hidden="true">*</span></label>
                        <InputText id="email" v-model="model.email" type="email" class="form-control" :class="{ 'p-invalid': fieldErrors.email }" aria-describedby="email-help email-error" required />
                        <small id="email-help" class="form-help">Use an address attendees or administrators can contact.</small>
                        <small v-if="fieldErrors.email" id="email-error" class="field-error">{{ fieldErrors.email }}</small>
                    </div>

                    <div class="form-group">
                        <label for="phoneNumber">Phone number</label>
                        <InputText id="phoneNumber" v-model="model.phoneNumber" class="form-control" aria-describedby="phone-number-help" />
                        <small id="phone-number-help" class="form-help">Optional. Include the country code when available.</small>
                    </div>

                    <div class="form-group">
                        <label for="address">Address</label>
                        <Textarea id="address" v-model="model.address" class="form-control" rows="3" aria-describedby="address-help" />
                        <small id="address-help" class="form-help">Optional. Include any location or meeting details that help delegates.</small>
                    </div>

                    <div class="row">
                        <div class="col-md-6">
                            <div class="form-group">
                                <label for="website">Website</label>
                                <InputText id="website" v-model="model.website" type="url" class="form-control" />
                            </div>
                        </div>
                        <div class="col-md-6">
                            <div class="form-group">
                                <label for="youtube">YouTube</label>
                                <InputText id="youtube" v-model="model.youtube" type="url" class="form-control" />
                            </div>
                        </div>
                    </div>

                    <div class="form-group">
                        <label for="status">Status</label>
                        <select id="status" class="form-control" v-model="model.status">
                            <option :value="1">Active</option>
                            <option :value="0">Inactive</option>
                        </select>
                        <small class="form-help">Only active exhibitors are shown in attendee-facing experiences.</small>
                    </div>

                    <div class="form-group mb-0">
                        <label for="exhibitor-banner">Banner</label>
                        <small class="form-help upload-help">Optional. Upload a JPG, PNG, or WEBP image up to 5 MB. A wide landscape image works best.</small>
                        <FileUpload
                            id="exhibitor-banner"
                            mode="advanced"
                            name="file"
                            :multiple="false"
                            accept="image/jpeg,image/png,image/webp"
                            :maxFileSize="5242880"
                            chooseLabel="Choose banner"
                            uploadLabel="Upload banner"
                            cancelLabel="Remove"
                            v-model="model.uploadFile"
                            :url="uploadUrl"
                            @select="markDirty"
                            @error="onUploadError"
                            @upload="onUploadSuccess"
                        />
                        <div v-if="imagePreviewUrl" class="banner-preview">
                            <img :src="imagePreviewUrl" alt="Current exhibitor banner">
                            <span>Current banner</span>
                        </div>
                    </div>
                </div>
            </section>
        </div>

        <div class="form-actions">
            <p class="form-actions-note">{{ hasUnsavedChanges ? 'You have unsaved changes.' : 'All changes are saved when you submit.' }}</p>
            <div class="form-actions-buttons">
                <button type="button" class="btn btn-outline-secondary mr-sm-1" @click="onCancel">Cancel</button>
                <button type="submit" class="btn btn-primary" :disabled="saving">
                    <span v-if="saving" class="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                    {{ saving ? 'Saving…' : (isNew ? 'Create exhibitor' : 'Save changes') }}
                </button>
            </div>
        </div>
    </form>

    <ConfirmDialog></ConfirmDialog>
</template>

<script>
import {defineComponent} from 'vue'
import FileUpload from "primevue/fileupload";
import apiService from "@/services/ApiService";
import InputText from "primevue/inputtext";
import Textarea from "primevue/textarea";

export default defineComponent({
    name: "ExhibitorForm",
    components: {
        FileUpload, InputText, Textarea
    },
    props: {
        id: {
            type: String,
            required: false
        },
        isNew: {
            type: Boolean,
            required: true
        }
    },
    data() {
        return {
            model: {
                id: '',
                boothNumber: '',
                companyName: '',
                companyDescription: '',
                productName: '',
                productDescription: '',
                address: '',
                contactPerson: '',
                email: '',
                phoneNumber: '',
                website: '',
                youtube: '',
                image: '',
                status: 1,
                uploadFile: ''
            },
            saving: false,
            hasUnsavedChanges: false,
            fieldErrors: {},
            uploadUrl: ''
        }
    },
    computed: {
        formErrorMessage() {
            return Object.values(this.fieldErrors)[0];
        },
        imagePreviewUrl() {
            return this.model.image ? `${apiService.defaults.baseURL}/file/${this.model.image}` : '';
        }
    },
    methods: {
        onSubmit(event) {
            event.preventDefault();
            if (!this.validateForm()) {
                return;
            }

            this.saving = true;
            let url = '/exhibitor/edit';
            let successMessage = 'Exhibitor has been updated';
            let errorMessage = 'Failed to update exhibitor';
            if(this.isNew){
                url = '/exhibitor/add';
                successMessage = 'Exhibitor has been added';
                errorMessage = 'Failed to add exhibitor';
            }

            const payload = {
                ...this.model,
                status: Number(this.model.status)
            };

            apiService.post(url, payload)
                .then(() => {
                    this.hasUnsavedChanges = false;
                    this.$toast.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: successMessage,
                        life: 5000
                    });
                })
                .catch(error => {
                    this.$toast.add({
                        severity: 'error',
                        summary: errorMessage,
                        detail: error.response?.data?.Message || 'Please try again.',
                        life: 5000
                    });
                })
                .finally(() => {
                    this.saving = false;
                });
        },
        onCancel() {
            if (!this.hasUnsavedChanges) {
                this.$router.push('/exhibitor');
                return;
            }

            this.$confirm.require({
                message: 'Discard your unsaved exhibitor changes?',
                header: 'Discard changes',
                icon: 'pi pi-exclamation-triangle',
                acceptClass: 'p-button-danger',
                accept: () => this.$router.push('/exhibitor')
            });
        },
        markDirty() {
            this.hasUnsavedChanges = true;
            if (Object.keys(this.fieldErrors).length) {
                this.validateForm();
            }
        },
        validateForm() {
            const errors = {};
            const requiredFields = [
                ['boothNumber', 'Booth number is required.'],
                ['companyName', 'Company name is required.'],
                ['companyDescription', 'Company description is required.'],
                ['productName', 'Product name is required.'],
                ['productDescription', 'Product description is required.'],
                ['contactPerson', 'Contact person is required.'],
                ['email', 'Email is required.']
            ];

            requiredFields.forEach(([field, message]) => {
                if (!String(this.model[field] || '').trim()) {
                    errors[field] = message;
                }
            });

            if (this.model.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.model.email.trim())) {
                errors.email = 'Enter a valid email address.';
            }

            this.fieldErrors = errors;
            return Object.keys(errors).length === 0;
        },
        onUploadSuccess(event){
            const response = JSON.parse(event.xhr.response);
            this.model.image = response.fileName;
            this.hasUnsavedChanges = true;

            this.$toast.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Banner uploaded.',
                life: 5000
            });
        },
        onUploadError(event){
            let response = {};
            try {
                response = JSON.parse(event.xhr.response);
            } catch (_) {
                response = {};
            }
            this.$toast.add({
                severity: 'error',
                summary: 'Failed to upload banner',
                detail: response.Message || 'Please choose a supported image and try again.',
                life: 5000
            });
        },
        getExhibitorById(id) {
            apiService.get(`/exhibitor/${id}`)
                .then(response => {
                    this.model = {
                        ...this.model,
                        ...response.data,
                        uploadFile: ''
                    };
                    this.hasUnsavedChanges = false;
                }).catch(error => {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Failed to get exhibitor',
                    detail: error.response?.data?.Message || 'Please try again.',
                    life: 5000
                });
            });
        }
    },
    mounted() {
        this.uploadUrl = `${apiService.defaults.baseURL}/file/upload`;
        if(!this.isNew){
            this.getExhibitorById(this.id);
        }
    }
})
</script>

<style scoped>
.exhibitor-form {
    max-width: 1280px;
}

.form-required-note {
    color: #5f6570;
    font-size: 0.875rem;
    margin: 0 0 1rem;
}

.form-required-note span,
.required-indicator {
    color: #c62828;
    font-weight: 700;
}

.form-alert {
    align-items: flex-start;
    background: #fff1f1;
    border: 1px solid #f5c2c7;
    border-radius: 0.5rem;
    color: #9d2525;
    display: flex;
    flex-direction: column;
    font-size: 0.9rem;
    gap: 0.2rem;
    margin-bottom: 1rem;
    padding: 0.8rem 1rem;
}

.exhibitor-form-section {
    background: #ffffff;
    border: 1px solid #e2e6ec;
    border-radius: 0.65rem;
    box-shadow: 0 0.15rem 0.65rem rgba(58, 59, 69, 0.05);
    height: 100%;
    padding: 1.5rem;
}

.section-heading {
    align-items: flex-start;
    border-bottom: 1px solid #edf0f4;
    display: flex;
    justify-content: space-between;
    margin-bottom: 1.5rem;
    padding-bottom: 1rem;
}

.section-heading h2 {
    color: #2b2f36;
    font-size: 1.15rem;
    font-weight: 600;
    margin: 0;
}

.section-eyebrow {
    color: #426cc6;
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    margin: 0 0 0.25rem;
}

.section-heading > .pi {
    color: #426cc6;
    font-size: 1.2rem;
    padding-top: 0.25rem;
}

.form-group label {
    color: #343a40;
    font-size: 0.9rem;
    font-weight: 600;
}

.form-help {
    color: #6c757d;
    display: block;
    font-size: 0.78rem;
    line-height: 1.35;
    margin-top: 0.35rem;
}

.upload-help {
    margin-bottom: 0.65rem;
}

.field-error {
    color: #c62828;
    display: block;
    font-size: 0.8rem;
    margin-top: 0.35rem;
}

.banner-preview {
    align-items: center;
    color: #5f6570;
    display: flex;
    font-size: 0.8rem;
    gap: 0.65rem;
    margin-top: 0.85rem;
}

.banner-preview img {
    border: 1px solid #e2e6ec;
    border-radius: 0.4rem;
    height: 3rem;
    object-fit: cover;
    width: 5.25rem;
}

.form-actions {
    align-items: center;
    border-top: 1px solid #e2e6ec;
    display: flex;
    justify-content: space-between;
    margin-top: 1.5rem;
    padding-top: 1.25rem;
}

.form-actions-note {
    color: #6c757d;
    font-size: 0.85rem;
    margin: 0;
}

.form-actions-buttons {
    display: flex;
}

@media (max-width: 767.98px) {
    .exhibitor-form-section {
        padding: 1.15rem;
    }

    .form-actions {
        align-items: stretch;
        flex-direction: column;
        gap: 1rem;
    }

    .form-actions-buttons {
        justify-content: flex-end;
    }
}
</style>
